import { TBoxNumber } from "libs/api/customer/src/lib/types/subscriptions";
import {
  ISubscriptionOverview,
  SubscriptionsAPI,
  TFreshMealPlan,
  TLitterPlan,
  getAllSubscriptionsByDeliveryDate,
  getNextDeliveryDateFromAllSubscriptions,
  isNibblesProduct,
} from "@api-clients/subscriptions";

import { SubscriptionStatus, SubscriptionType } from "@/primary-models";

import {
  CustomerStatus,
  ECustomerMultiCatStatus,
  ExtraSubscriptionStatus,
  FreshSubscriptionStatus,
  TPlanGift,
} from "./types";
import {
  LITTER_PLAN_GIFT_BY_BOX_NUMBER,
  MEAL_PLAN_GIFT_BY_BOX_NUMBER,
} from "./constants";



export class CustomerState {

  /**
   * A class to determine any customer's status.
   * Status being a culmination of their litter/nibbles/fresh subscription (or lack thereof).
   * e.g. Active fresh, never had scoop (PreInactive), and Inactive nibbles.
   */
  constructor(
    private readonly subscriptionsClient: SubscriptionsAPI,
  ) { }

  public async getCustomerStatus(): Promise<CustomerStatus> {
    try {
      const data = await this.subscriptionsClient.getSubscriptionsOverview();
      return CustomerState.determineCustomerStatus(data);
    } catch (error) {

      console.error(error);
    }
    return {
      freshStatus: FreshSubscriptionStatus.UNKNOWN,
      nibblesStatus: ExtraSubscriptionStatus.UNKNOWN,
      scoopStatus: ExtraSubscriptionStatus.UNKNOWN,
      multiCatStatus: ECustomerMultiCatStatus.UNKNOWN,
    };
  }

  public async getBoxNumbersPerCat(): Promise<Map<number, TBoxNumber>> {
    try {
      const data = await this.subscriptionsClient.getSubscriptionsOverview();
      return CustomerState.determineBoxNumbers(data);
    } catch (error) {

      console.error(error);
    }
    return new Map<number, TBoxNumber>();
  }

  public static determineBoxNumbers(subscriptions: ISubscriptionOverview): Map<number, TBoxNumber> {
    const boxNumbers = new Map<number, TBoxNumber>();
    subscriptions.freshMealPlans.forEach((freshPlan) => {
      const { boxesFulfilled } = freshPlan.fulfillment;
      boxNumbers.set(freshPlan.cat.id, boxesFulfilled);
    });
    return boxNumbers;
  }

  public static getNextPlanToBeDelivered(
    subscriptions: ISubscriptionOverview,
  ): TFreshMealPlan | TLitterPlan | undefined {
    const nextDelivery = getNextDeliveryDateFromAllSubscriptions(subscriptions);
    if (!nextDelivery) {
      return undefined;
    }

    const nextPlan = getAllSubscriptionsByDeliveryDate(subscriptions, nextDelivery)[0];
    return nextPlan;
  }

  public static getNextDeliveryGift(subscriptions: ISubscriptionOverview): TPlanGift | undefined {
    const nextPlanForDelivery = CustomerState.getNextPlanToBeDelivered(subscriptions);
    const boxNumber = nextPlanForDelivery?.fulfillment.boxesFulfilled;

    if (!boxNumber) {
      return;
    }

    let gift: TPlanGift | undefined;

    const isScoop = nextPlanForDelivery.type === SubscriptionType.SCOOP_HEALTH
      || nextPlanForDelivery.type === SubscriptionType.SCOOP_PLANET;

    if (nextPlanForDelivery.type === SubscriptionType.FRESH) {
      gift = MEAL_PLAN_GIFT_BY_BOX_NUMBER[boxNumber];
    } else if (isScoop) {
      gift = LITTER_PLAN_GIFT_BY_BOX_NUMBER[boxNumber];
    }

    return gift;
  }

  public static determineCustomerStatus(
    customerSubscriptions: ISubscriptionOverview,
  ): CustomerStatus {

    const { freshMealPlans, litterPlans } = customerSubscriptions;

    const freshStatus = CustomerState.determineFreshStatus(
      freshMealPlans,
    );
    const nibblesStatus = CustomerState.determineNibblesStatus(
      freshMealPlans,
    );
    const scoopStatus = CustomerState.determineScoopStatus(
      litterPlans,
    );
    const multiCatStatus = CustomerState.determineMultiCatStatus(freshMealPlans);
    return {
      freshStatus,
      nibblesStatus,
      scoopStatus,
      multiCatStatus,
    };
  }

  public static determineMultiCatStatus(freshSubscriptions: TFreshMealPlan[]): ECustomerMultiCatStatus {
    if (freshSubscriptions.length < 2) {
      return ECustomerMultiCatStatus.NEVER;
    }
    if (freshSubscriptions.filter(
      (plan) => plan.status === SubscriptionStatus.ACTIVE,
    ).length < 2) {
      return ECustomerMultiCatStatus.INACTIVE;
    }
    return ECustomerMultiCatStatus.ACTIVE;
  }

  public static determineNibblesStatus(
    freshMealPlans: TFreshMealPlan[],
  ): ExtraSubscriptionStatus {

    const activeFresh = freshMealPlans.filter(
      (fs) => fs.status === SubscriptionStatus.ACTIVE,
    );

    const filteredExtras = activeFresh.filter(
      (mealPlan) => mealPlan.items.some(({ name }) => isNibblesProduct(name)),
    );

    return filteredExtras.length
      ? ExtraSubscriptionStatus.Active
      : ExtraSubscriptionStatus.Inactive;
  }

  public static doesNotHaveAnyActiveFresh(freshSubscriptions: TFreshMealPlan[]): boolean {
    const hasActiveFresh = freshSubscriptions.some((fs) => fs.status === SubscriptionStatus.ACTIVE);
    return !hasActiveFresh;
  }

  public static hasTrialPlan(freshSubscriptions: TFreshMealPlan[]): boolean {
    return freshSubscriptions.some((
      (subscription) => subscription.fulfillment.chargeCount === 1
      && subscription.fulfillment.boxesFulfilled === 0));
  }

  public static determineFreshStatus(
    freshSubscriptions: TFreshMealPlan[],
  ): FreshSubscriptionStatus {
    if (!freshSubscriptions.length) {
      return FreshSubscriptionStatus.Never;
    }

    const isTrial = CustomerState.hasTrialPlan(freshSubscriptions);
    const hasNoActiveFresh = CustomerState.doesNotHaveAnyActiveFresh(freshSubscriptions);

    if (hasNoActiveFresh && isTrial) {
      return FreshSubscriptionStatus.InactiveAwaitingTrial;
    }
    if (hasNoActiveFresh && !isTrial) {
      return FreshSubscriptionStatus.Inactive;
    }

    if (isTrial) {
      return FreshSubscriptionStatus.ActiveAwaitingTrial;
    }

    return FreshSubscriptionStatus.Active;
  }

  public static determineScoopStatus(
    scoopSubscriptions: TLitterPlan[],
  ): ExtraSubscriptionStatus {
    if (!scoopSubscriptions.length) {
      return ExtraSubscriptionStatus.Never;
    }
    return scoopSubscriptions.some((es) => es.status === SubscriptionStatus.ACTIVE)
      ? ExtraSubscriptionStatus.Active
      : ExtraSubscriptionStatus.Inactive;
  }

}
